/**
 * ChartOverlayHost — universal "open client chart WITHOUT leaving the
 * screen" host (Jun 2026 user request: closing a chart must return the
 * user to the exact screen/tab/filters they came from, everywhere).
 *
 * Mounted once in DashboardLayoutV2.  Any component can call
 * `openChartOverlay(clientId, tab)`; the chart renders as a dialog ON
 * TOP of the current route — the underlying screen never unmounts, so
 * every filter, sort, page and scroll position survives the round trip.
 *
 * The dialog drives its active tab through the `tab` search param, so
 * the host snapshots the origin's `tab`/`action` params on open and
 * restores them on close.  New tabs get this behavior for free — just
 * call openChartOverlay instead of navigating to /v2/clients/:id.
 */
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

const ClientChartV2Dialog = lazy(() => import('./ClientChartV2Dialog'));

export const openChartOverlay = (clientId, tab, params) => {
  if (!clientId) return;
  window.dispatchEvent(new CustomEvent('v2:open-chart', { detail: { clientId, tab, params } }));
};

export const ChartOverlayHost = () => {
  const [overlay, setOverlay] = useState(null);
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const prevParamsRef = useRef(null);

  useEffect(() => {
    const onOpen = (e) => {
      const { clientId, tab, params } = e.detail || {};
      if (!clientId) return;
      const sp = new URLSearchParams(window.location.search);
      const toSet = { ...(params || {}) };
      if (tab) toSet.tab = tab;
      const prev = { tab: sp.get('tab'), action: sp.get('action') };
      Object.keys(toSet).forEach((k) => { prev[k] = sp.get(k); });
      Object.entries(toSet).forEach(([k, v]) => { if (v != null && v !== '') sp.set(k, v); });
      prevParamsRef.current = prev;
      setSearchParams(sp, { replace: true });
      setOverlay({ clientId });
    };
    window.addEventListener('v2:open-chart', onOpen);
    return () => window.removeEventListener('v2:open-chart', onOpen);
  }, [setSearchParams]);

  // Route change (e.g., Switch Client inside the chart navigates to
  // /v2/clients/:id) — the overlay yields to the route-mounted chart.
  const pathname = location.pathname;
  useEffect(() => { setOverlay(null); }, [pathname]);

  const handleClose = () => {
    setOverlay(null);
    const sp = new URLSearchParams(window.location.search);
    const prev = prevParamsRef.current || {};
    Object.keys(prev).forEach((k) => {
      if (prev[k]) sp.set(k, prev[k]);
      else sp.delete(k);
    });
    prevParamsRef.current = null;
    setSearchParams(sp, { replace: true });
  };

  if (!overlay) return null;
  return (
    <Suspense fallback={null}>
      <ClientChartV2Dialog clientId={overlay.clientId} onClose={handleClose} />
    </Suspense>
  );
};
